"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Beaker, BookOpen, CalendarRange, FileText, GraduationCap, MessageSquare, Sparkles, type LucideIcon } from "lucide-react";
import { AnalyticsInfoTip } from "@/components/analytics/analytics-stat-tile";
import { ErrorMessage } from "@/components/ui/error-message";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useDashboardMetrics } from "@/hooks/useDashboardMetrics";
import { cn } from "@/lib/utils";
function SectionEyebrow(t0) {
  const $ = _c(6);
  const {
    children,
    className,
    id
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("text-[11px] font-semibold uppercase tracking-wider text-foreground/55", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== id || $[4] !== t1) {
    t2 = <p id={id} className={t1}>{children}</p>;
    $[2] = children;
    $[3] = id;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function StatItem(t0) {
  const $ = _c(17);
  const {
    icon: Icon,
    label,
    value,
    titleTooltip,
    tooltipsEnabled
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden={true} />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span>{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== label || $[5] !== titleTooltip || $[6] !== tooltipsEnabled) {
    t3 = titleTooltip && tooltipsEnabled ? <AnalyticsInfoTip label={`About ${label}`}>{titleTooltip}</AnalyticsInfoTip> : null;
    $[4] = label;
    $[5] = titleTooltip;
    $[6] = tooltipsEnabled;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <div className="flex items-center gap-1.5 text-xs font-medium text-foreground/65">{t1}{t2}{t3}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== value) {
    t5 = <p className="mt-1.5 text-2xl font-semibold tabular-nums leading-none tracking-tight text-foreground">{value}</p>;
    $[12] = value;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = <div className="bg-background px-3.5 py-3">{t4}{t5}</div>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
export interface WorkspaceAnalyticsGlanceProps {
  className?: string;
}

/**
 * Launcher analytics for `/dashboard`: median learning cycle + readable inline counts.
 * Always visible, no cards-as-buttons, popovers, or side panels.
 */
export function WorkspaceAnalyticsGlance(t0) {
  const $ = _c(64);
  const {
    className
  } = t0;
  const metrics = useDashboardMetrics();
  const [tooltipsEnabled, setTooltipsEnabled] = React.useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const timeout = window.setTimeout(() => setTooltipsEnabled(true), 1500);
      return () => window.clearTimeout(timeout);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  React.useEffect(t1, t2);
  if (metrics.error) {
    let t3;
    if ($[2] !== className || $[3] !== metrics.error) {
      t3 = <ErrorMessage message={metrics.error} className={className} />;
      $[2] = className;
      $[3] = metrics.error;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC" : "UTC";
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const browserTz = t3;
  const loading = metrics.loading;
  let t4;
  if ($[6] !== loading) {
    t4 = n => loading ? "\u2026" : String(n);
    $[6] = loading;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const value = t4;
  const hasMedian = !loading && metrics.medianLearningCycleDays != null;
  let t5;
  bb0: {
    if (loading) {
      t5 = "Loading workspace analytics\u2026";
      break bb0;
    }
    if (metrics.learningCycleSampleSize > 0) {
      const n_0 = metrics.learningCycleSampleSize;
      t5 = `Based on ${n_0} learning${n_0 === 1 ? "" : "s"} with linked assumptions.`;
      break bb0;
    }
    if (metrics.learnings > 0) {
      t5 = "No qualifying learnings yet \u2014 hypothesis needs linked assumptions.";
      break bb0;
    }
    t5 = "Appears when learnings include assumptions on their hypotheses.";
  }
  const medianDetail = t5;
  let t6;
  bb1: {
    if (loading) {
      t6 = "Loading\u2026";
      break bb1;
    }
    const base = `Across all projects in this workspace: for each qualifying learning we use the earliest assumption on its hypothesis and the learning timestamp, count whole calendar days between them in ${browserTz}, then take the median.`;
    if (metrics.learningCycleSampleSize > 0) {
      const n_1 = metrics.learningCycleSampleSize;
      t6 = `${base} Sample size: ${n_1} learning${n_1 === 1 ? "" : "s"}.`;
      break bb1;
    }
    t6 = base;
  }
  const medianTooltip = t6;
  let t7;
  if ($[8] !== className) {
    t7 = cn("space-y-6", className);
    $[8] = className;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="space-y-1"><h2 className="text-lg font-semibold tracking-tight text-foreground">Projects</h2><p className="text-sm text-muted-foreground">Open a project to continue where you left off.</p></div>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = !tooltipsEnabled;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <SectionEyebrow id="workspace-activity-heading">Workspace activity · all-time</SectionEyebrow>;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <CalendarRange className="size-4 shrink-0 text-muted-foreground" aria-hidden={true} />;
    t12 = <span>Median learning cycle</span>;
    $[12] = t11;
    $[13] = t12;
  } else {
    t11 = $[12];
    t12 = $[13];
  }
  let t13;
  if ($[14] !== medianTooltip || $[15] !== tooltipsEnabled) {
    t13 = tooltipsEnabled ? <AnalyticsInfoTip label="How median learning cycle is calculated for this workspace">{medianTooltip}</AnalyticsInfoTip> : null;
    $[14] = medianTooltip;
    $[15] = tooltipsEnabled;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== t13) {
    t14 = <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">{t11}{t12}{t13}</div>;
    $[17] = t13;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== hasMedian || $[20] !== loading || $[21] !== metrics.medianLearningCycleDays) {
    t15 = hasMedian ? <p className="mt-2 text-3xl font-semibold tabular-nums tracking-tight text-foreground">{metrics.medianLearningCycleDays} days</p> : <p className="mt-2 text-base font-medium text-muted-foreground">{loading ? "\u2026" : "Not available yet"}</p>;
    $[19] = hasMedian;
    $[20] = loading;
    $[21] = metrics.medianLearningCycleDays;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== medianDetail) {
    t16 = <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{medianDetail}</p>;
    $[23] = medianDetail;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t14 || $[26] !== t15 || $[27] !== t16) {
    t17 = <div className="rounded-lg border border-border/40 bg-background/70 px-3.5 py-3.5">{t14}{t15}{t16}</div>;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <SectionEyebrow>Totals</SectionEyebrow>;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  const t19 = value(metrics.projects);
  let t20;
  if ($[30] !== t19 || $[31] !== tooltipsEnabled) {
    t20 = <StatItem icon={BookOpen} label="Projects" value={t19} tooltipsEnabled={tooltipsEnabled} titleTooltip="Projects in this workspace with deleted projects excluded." />;
    $[30] = t19;
    $[31] = tooltipsEnabled;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const t21 = value(metrics.chats);
  let t22;
  if ($[33] !== t21 || $[34] !== tooltipsEnabled) {
    t22 = <StatItem icon={MessageSquare} label="Chats" value={t21} tooltipsEnabled={tooltipsEnabled} titleTooltip="Conversations in workspace projects, excluding deleted chats." />;
    $[33] = t21;
    $[34] = tooltipsEnabled;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  const t23 = value(metrics.experiments);
  let t24;
  if ($[36] !== t23 || $[37] !== tooltipsEnabled) {
    t24 = <StatItem icon={Beaker} label="Experiments" value={t23} tooltipsEnabled={tooltipsEnabled} titleTooltip="All experiments in workspace projects, every status including Draft." />;
    $[36] = t23;
    $[37] = tooltipsEnabled;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  const t25 = value(metrics.learnings);
  let t26;
  if ($[39] !== t25 || $[40] !== tooltipsEnabled) {
    t26 = <StatItem icon={GraduationCap} label="Learnings" value={t25} tooltipsEnabled={tooltipsEnabled} titleTooltip="Synthesized learning cards across workspace projects." />;
    $[39] = t25;
    $[40] = tooltipsEnabled;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  const t27 = value(metrics.insights);
  let t28;
  if ($[42] !== t27 || $[43] !== tooltipsEnabled) {
    t28 = <StatItem icon={Sparkles} label="Insights" value={t27} tooltipsEnabled={tooltipsEnabled} titleTooltip="Insights extracted from learning cards in workspace projects." />;
    $[42] = t27;
    $[43] = tooltipsEnabled;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  const t29 = value(metrics.documents);
  let t30;
  if ($[45] !== t29 || $[46] !== tooltipsEnabled) {
    t30 = <StatItem icon={FileText} label="Documents" value={t29} tooltipsEnabled={tooltipsEnabled} titleTooltip="Documents uploaded or linked to workspace projects." />;
    $[45] = t29;
    $[46] = tooltipsEnabled;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  if ($[48] !== t20 || $[49] !== t22 || $[50] !== t24 || $[51] !== t26 || $[52] !== t28 || $[53] !== t30) {
    t31 = <div className="space-y-2.5">{t18}<dl className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border/40 bg-border/40 sm:grid-cols-3">{t20}{t22}{t24}{t26}{t28}{t30}</dl></div>;
    $[48] = t20;
    $[49] = t22;
    $[50] = t24;
    $[51] = t26;
    $[52] = t28;
    $[53] = t30;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] !== t17 || $[56] !== t31) {
    t32 = <section className="space-y-4 rounded-xl border border-border/45 bg-muted/25 px-4 py-4" aria-labelledby="workspace-activity-heading">{t10}{t17}{t31}</section>;
    $[55] = t17;
    $[56] = t31;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] !== t32 || $[59] !== t9) {
    t33 = <TooltipProvider delayDuration={250} disableHoverableContent={t9}>{t32}</TooltipProvider>;
    $[58] = t32;
    $[59] = t9;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] !== t33 || $[62] !== t7) {
    t34 = <header className={t7}>{t8}{t33}</header>;
    $[61] = t33;
    $[62] = t7;
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  return t34;
}